/**
 * Every pixel the Progress Alt grid measures itself by.
 *
 * These live together because they have to agree with each other: the date column's width sets the
 * scrollbar gutter's width, which sets where the grid's content begins, which sets where a header
 * bar parks when it goes sticky. Held separately they drift, and a drift of a single pixel shows up
 * as a divider that doesn't line up with the one above it.
 */

export const GAP_PX = 8;
export const CANVAS_PADDING_X_PX = 16;

export const TILE_RADIUS_PX = 6;
export const COLLAPSED_ROW_HEIGHT_PX = 36;

const SMALL_COLUMN_WIDTH_PX = 232;
const SMALL_TILE_HEIGHT_PX = 142;

/** How much bigger a large thumbnail is than a small one, in both dimensions. */
const LARGE_TILE_SCALE = 2;

export type ProgressAltTileSizeId = 'small' | 'large';

/** How wide a column and how tall a row the grid draws at the chosen thumbnail size. */
export type ProgressAltTileSize = { columnWidthPx: number; tileHeightPx: number };

export const getProgressAltTileSize = (tileSizeId: ProgressAltTileSizeId): ProgressAltTileSize =>
  tileSizeId === 'large'
    ? {
      columnWidthPx: Math.round(SMALL_COLUMN_WIDTH_PX * LARGE_TILE_SCALE),
      tileHeightPx: Math.round(SMALL_TILE_HEIGHT_PX * LARGE_TILE_SCALE),
    }
    : { columnWidthPx: SMALL_COLUMN_WIDTH_PX, tileHeightPx: SMALL_TILE_HEIGHT_PX };

/**
 * One height for every control the panel's toolbars carry — segmented options, the collapsed period
 * chip, the date column's collapse toggle. Set on the outer box rather than reached via padding, so a
 * control that grows an icon stays level with the ones beside it instead of outgrowing them.
 */
export const CONTROL_HEIGHT_PX = 25;

const HEADER_BOX_HEIGHT_PX = 48;
export const HEADER_PADDING_TOP_PX = 10;
export const HEADER_HEIGHT_PX = HEADER_BOX_HEIGHT_PX + HEADER_PADDING_TOP_PX;

export const ROW_LABEL_PADDING_TOP_PX = 10;
/** Less than the padding it sits inside on every other edge: the caret's own weight makes up the rest. */
export const ROW_LABEL_PADDING_LEFT_PX = 8;
export const ROW_LABEL_PADDING_RIGHT_PX = 10;

export const SCROLL_BAR_HEIGHT_PX = 38;

/**
 * The photo view's frame. Its left edge lines up with the grid's — the time navigator sits where the
 * date column sits, so the reader's eye finds the timeline in the same place in both modes.
 */
export const PHOTO_VIEW_DETAILS_WIDTH_PX = 340;
export const PHOTO_VIEW_FILMSTRIP_HEIGHT_PX = 132;
/** Wide enough for a whole row label — the time buttons name where they go, not just which way. */
export const PHOTO_VIEW_TIME_NAV_WIDTH_PX = 190;
export const PHOTO_VIEW_THUMBNAIL_WIDTH_PX = 132;

/**
 * How far either side of the bucket on screen the time navigator reaches. Two is what fits without the
 * stack outgrowing the strip beside it, and enough to read as a stretch of timeline rather than a pair
 * of arrows.
 */
export const PHOTO_VIEW_TIME_NAV_REACH = 2;

/**
 * How far a sticky element holds off the edge it is pinned against — and, for the same reason, how much
 * clear air a cell scrolled into view keeps between itself and the edge it was brought in over.
 */
export const STICKY_INSET_PX = 10;

/**
 * Fixed, not derived from its contents. As a flex item the date column's `min-width: auto` would
 * otherwise let a long date inflate it past its basis, and everything aligned to it — the scrollbar
 * gutter, the sticky header offset — would be measuring a width the column no longer has.
 */
const DATE_COLUMN_WIDTH_PX = 220;

/**
 * Barely more than a caret and two digits. The whole point of collapsing is to hand the width back
 * to the photos, so the column shrinks to about a quarter rather than merely narrowing.
 */
const COLLAPSED_DATE_COLUMN_WIDTH_PX = 60;

/**
 * How wide the period chip may grow inside the collapsed column. Capped rather than filling the cell,
 * so the one control up there sits over the row carets below instead of spanning past them.
 */
export const COLLAPSED_PERIOD_CHIP_MAX_WIDTH_PX = 40;

export const getDateColumnWidth = (collapsed: boolean): number => collapsed ? COLLAPSED_DATE_COLUMN_WIDTH_PX : DATE_COLUMN_WIDTH_PX;

/** Where the grid's own content begins, once the date column and the gap beside it are accounted for. */
export const getGridLeft = (dateColumnWidthPx: number): number => dateColumnWidthPx + GAP_PX;

/**
 * Everything in a header bar that is not title text: the side paddings (9 + 6), the borders (2), and
 * clearance for the caret posted over the bar's end (22 wide with 6 of air) — out of the title's
 * layout, but still ground the shoved title must not slide under.
 */
const HEADER_NON_TITLE_WIDTH_PX = 45;

/**
 * How wide the header's sticky title may grow: one column, less the bar's other furniture. Capped so
 * a parked title is shoved along exactly when a single column's width of its bar remains — from there
 * the title fills the bar's last column and slides off with it.
 */
export const getHeaderTitleMaxWidth = ({ columnWidthPx }: ProgressAltTileSize): number => columnWidthPx - HEADER_NON_TITLE_WIDTH_PX;

/**
 * Where each of a run of tracks begins, given where the run starts and the gap the grid puts between
 * them. Serves both axes: hand it column widths from the grid's left edge, or row heights from below
 * the header row.
 */
export const getRunningOffsets = (trackSizesPx: readonly number[], startPx: number): number[] => {
  let offset = startPx;
  return trackSizesPx.map(size => {
    const at = offset;
    offset += size + GAP_PX;
    return at;
  });
};

/** How wide a column sits, opened out to lay every photo of its widest cell side by side or not. */
export const getColumnWidth = (
  expanded: boolean,
  maxPhotoCount: number,
  { columnWidthPx }: ProgressAltTileSize,
): number => expanded ? maxPhotoCount * columnWidthPx + (maxPhotoCount - 1) * GAP_PX : columnWidthPx;
